import { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import {
  buildAwareDeviceConfig,
  buildCustomChartConfig,
  buildStandardChartConfig,
  buildWeatherChartConfig,
} from './chartConfigs/chartConfigStrategies.jsx';
import { EChartsErrorBoundary } from './chartUtils/EChartsErrorBoundary.jsx';

/**
 * ECharts-based modular chart component
 * Supports multiple chart configurations through strategy pattern:
 * - Standard: Basic line/bar charts
 * - Weather: Charts with weather icons and wind direction
 * - Aware: Device threshold monitoring with colored zones
 * - Custom: User-defined configurations
 *
 * @param {string} type - Chart type: "line" | "bar"
 * @param {object} dataSets - Chart.js compatible dataset format
 * @param {object} options - Chart.js compatible options with ECharts extensions
 * @param {string} chartStrategy - Override auto-detection: "standard" | "weather" | "aware" | "custom"
 * @param {function} customConfigBuilder - Custom config builder function(context)
 */
const EChartsMixed = ({
  type = 'line',
  dataSets = {},
  options = {},
  setUnitType,
  unitType,
  selectedDataType,
  getUnitType,
  keyString,
  style,
}) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [chartOptions, setChartOptions] = useState({});
  const [containerReady, setContainerReady] = useState(false);
  const hasExplicitHeight = style?.height !== undefined && style?.height !== null;

  // Wait for container to have dimensions
  useEffect(() => {
    let observer = null;
    let mounted = true;
    let timer = null;

    const checkContainer = () => {
      if (!mounted || !containerRef.current) return false;

      const { clientWidth, clientHeight } = containerRef.current;
      if (clientWidth > 0 && clientHeight > 0) {
        if (mounted && !containerReady) {
          setContainerReady(true);
        }
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkContainer()) return;

    // Use ResizeObserver for better detection
    if (containerRef.current) {
      observer = new ResizeObserver(() => {
        if (mounted) {
          checkContainer();
        }
      });

      observer.observe(containerRef.current);

      // Also check after a short delay as fallback
      timer = setTimeout(() => {
        if (mounted) {
          checkContainer();
        }
      }, 150);
    }

    return () => {
      mounted = false;
      if (timer) {
        clearTimeout(timer);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [containerReady]);

  // Handle chart click events
  const handleChartClick = (params) => {
    if (options?.onClick && params.componentType === 'series') {
      // Create a Chart.js compatible event object
      const evt = {
        x: params.event.offsetX,
        y: params.event.offsetY,
        chart: {
          _sortedMetasets: dataSets.datasets.map((ds) => ({
            _dataset: ds,
          })),
        },
      };

      const element = [
        {
          index: params.dataIndex,
          datasetIndex: params.seriesIndex,
          element: {
            y: params.event.offsetY,
          },
        },
      ];

      options.onClick(evt, element);
    }
  };

  // Convert Chart.js data format to ECharts format
  const convertDataSetsToECharts = useMemo(() => {
    // Check for datasets - they might be in dataSets.datasets or just dataSets
    const datasets = dataSets?.datasets || [];

    if (!datasets || datasets.length === 0) {
      console.warn('EChartsMixed: No datasets available');
      return { xAxisData: [], series: [] };
    }

    // X-axis labels can come from multiple places:
    // 1. dataSets.labels (standard)
    // 2. options.scales.x.labels (weather charts)
    // 3. First dataset's data (if using {x, y} format)
    const labelsFromDataSets = dataSets?.labels;
    const labelsFromOptions = options?.scales?.x?.labels;

    let xAxisData = labelsFromDataSets || labelsFromOptions || [];

    const series = datasets.map((dataset, _datasetIndex) => {
      const chartType = dataset.type || type;

      // Extract numeric values and x-axis labels from various data formats
      const extractValue = (d) => {
        if (d === null || d === undefined) return null;
        if (typeof d === 'number') return d;
        if (d.value !== undefined) return d.value;
        if (d.y !== undefined) return d.y;
        return d;
      };

      // Check if data uses {x, y} format
      const firstDataPoint = dataset.data?.[0];
      const hasXValues =
        firstDataPoint && typeof firstDataPoint === 'object' && firstDataPoint.x !== undefined;

      let actualXData = null;
      let actualYData = [];

      if (hasXValues) {
        // Extract both x and y values
        actualXData = dataset.data.map((d) => d.x);
        actualYData = dataset.data.map((d) => d.y);

        // Use this dataset's x values if we don't have xAxisData yet
        if (xAxisData.length === 0) {
          xAxisData = actualXData;
        }
      } else {
        // Just extract y values
        actualYData = dataset.data?.map(extractValue) || [];
      }

      return {
        name: dataset.label,
        type: chartType === 'line' ? 'line' : 'bar',
        data: actualYData,
        xAxisData: actualXData, // Store x-axis data if it exists
        _meta: dataset._meta, // Preserve metadata for click handlers
        _rawData: dataset.data, // Store original data for tooltips
        smooth: dataset.tension > 0,
        showSymbol: true, // Always show symbols for data points
        symbolSize: dataset.pointRadius || 6,
        itemStyle: {
          color: dataset.backgroundColor || dataset.borderColor,
          borderColor: dataset.borderColor,
          borderWidth: dataset.borderWidth || 0,
        },
        lineStyle: {
          color: dataset.borderColor,
          width: dataset.borderWidth || 2,
          type: dataset.borderDash?.length > 0 ? 'dashed' : 'solid',
        },
        areaStyle: dataset.fill
          ? {
              color: dataset.backgroundColor,
              opacity: 0.3,
            }
          : undefined,
        yAxisIndex: 0, // Will be updated later
        stack: dataset.stack,
      };
    });

    // If we still don't have xAxisData, create indices
    if (xAxisData.length === 0 && series.length > 0 && series[0].data.length > 0) {
      xAxisData = series[0].data.map((_, idx) => idx.toString());
      console.warn('EChartsMixed: No labels found, using indices');
    }

    return { xAxisData, series };
  }, [dataSets, options, type]);

  // Build ECharts option configuration using strategy pattern
  useEffect(() => {
    const { xAxisData, series } = convertDataSetsToECharts;

    // console.log("TEMP xAxisData:", xAxisData);
    // console.log("TEMP series:", series);

    if (!xAxisData || xAxisData.length === 0) {
      console.warn('EChartsMixed: No x-axis data available');
      return;
    }

    if (!series || series.length === 0) {
      console.warn('EChartsMixed: No series data available');
      return;
    }

    // Determine chart configuration strategy based on options/plugins
    let chartConfig;
    const configContext = {
      xAxisData,
      series,
      dataSets,
      options,
      type,
    };

    // Select appropriate config builder based on chart type/plugins
    if (options?.plugins?.verticalLinePlugin?.enabled) {
      // console.log("Building redline aware device chart config");
      // Aware device charts with threshold visualization
      chartConfig = buildAwareDeviceConfig(configContext);
    } else if (
      options?.plugins?.weatherIconAtPoint?.enabled ||
      options?.plugins?.windDirectionIcon?.enabled
    ) {
      // Weather charts with icons and wind direction
      chartConfig = buildWeatherChartConfig(configContext);
    } else if (options?.chartConfigType) {
      // Custom chart configuration
      chartConfig = buildCustomChartConfig(configContext, options.chartConfigType);
    } else {
      // Standard line/bar chart
      chartConfig = buildStandardChartConfig(configContext);
    }

    setChartOptions(chartConfig);
  }, [convertDataSetsToECharts, options, dataSets, type]);

  const handleResetZoom = () => {
    if (chartRef.current) {
      try {
        const instance = chartRef.current.getEchartsInstance();
        if (instance) {
          instance.dispatchAction({
            type: 'dataZoom',
            start: 0,
            end: 100,
          });
        }
      } catch (error) {
        console.warn('Error resetting zoom:', error);
      }
    }
  };
  return (
    <div
      className="d-flex flex-column w-100 h-100"
      style={{
        minHeight: hasExplicitHeight ? 0 : '500px',
        backgroundColor: 'white',
        minWidth: 0,
        ...style,
      }}
    >
      <div
        className="d-flex justify-content-between p-2 flex-shrink-0"
        style={{ backgroundColor: 'white' }}
      >
        {setUnitType && getUnitType && (
          <Button size="sm" variant="primary" onClick={() => setUnitType(!unitType)}>
            Units: {getUnitType(unitType, selectedDataType)}
          </Button>
        )}

        <Button size="sm" variant="primary" onClick={handleResetZoom}>
          Reset Zoom
        </Button>
      </div>

      <div
        ref={containerRef}
        className="flex-grow-1 w-100"
        style={{
          minHeight: hasExplicitHeight ? 0 : '450px',
          height: hasExplicitHeight ? '100%' : '450px',
          position: 'relative',
          backgroundColor: 'white',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {containerReady &&
        containerRef.current &&
        Object.keys(chartOptions).length > 0 &&
        chartOptions.series?.length > 0 ? (
          <EChartsErrorBoundary
            ref={chartRef}
            option={chartOptions}
            style={{ height: '100%', width: '100%' }}
            notMerge
            lazyUpdate
            opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
            onEvents={{
              click: handleChartClick,
            }}
            key={keyString || 'default'}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading chart...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EChartsMixed;
